/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Button, ModalConfirm } from "@nordcloud/gnui";
import { KeyValue } from "~/generated/graphql";
import { useDisclosure } from "~/hooks";
import { showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { Props } from "./types";

type MetadataDeleteProps<T> = Props<T> & { selected: KeyValue };

export function MetadataDelete<T>({
  updateHook,
  metadata,
  id,
  selected,
}: MetadataDeleteProps<T>) {
  const { isOpen, close, open } = useDisclosure(false);

  const [updateGroup] = updateHook({
    onSuccess: async () => {
      showSuccess(generateActionSuccessText("Metadata")()("deleted")());
      close();
    },
  });

  const onDelete = ({ key, value }: KeyValue) => {
    const data =
      metadata
        ?.map((meta) => ({ key: meta.key, value: meta.value }))
        .filter((meta) => meta.key !== key && meta.value !== value) ?? [];

    updateGroup({
      id: id,
      metadata: [...data],
    } as T);
  };

  return (
    <>
      <Button
        icon="trash"
        severity="low"
        onClick={open}
        data-testid="Delete Metadata"
      />
      <ModalConfirm
        onClose={close}
        isOpen={isOpen}
        confirm={() => onDelete({ key: selected.key, value: selected.value })}
        actionLabel="Delete"
        contentLabel={`Delete Metadata`}
      >
        {`Are you sure you want to delete?`}
        <br />
        {`${selected.key}, ${selected.value}`}
      </ModalConfirm>
    </>
  );
}
